import { useContext } from "react";
import { BreadcrumbContext, BreadcrumbContextType } from "./BreadcrumbContext";

/**
 * Custom hook to access and manage breadcrumb navigation state
 *
 * This hook provides access to the breadcrumb context and all its methods.
 * It must be used within a BreadcrumbProvider.
 *
 * @returns BreadcrumbContextType object with breadcrumbs state and methods
 * @throws Error if used outside of BreadcrumbProvider
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { breadcrumbs, setBreadcrumbs, addBreadcrumb } = useBreadcrumb();
 *
 *   useEffect(() => {
 *     setBreadcrumbs([
 *       { label: 'Home', path: '/' },
 *       { label: 'Current Page', isActive: true }
 *     ]);
 *   }, [setBreadcrumbs]);
 *
 *   return <div>Content</div>;
 * };
 * ```
 */
export const useBreadcrumb = (): BreadcrumbContextType => {
    const context = useContext(BreadcrumbContext);

    if (context === undefined) {
        throw new Error(
            "useBreadcrumb must be used within a BreadcrumbProvider. " +
                "Make sure your component is wrapped with <BreadcrumbProvider>."
        );
    }

    return context;
};
